import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Chip,
  Link,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { fmtDateTime } from './formatDate';
import type { TransactionDetailDto } from './transactionDetailTypes';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmt = (n?: number | null, decimals = 2) =>
  n == null ? null : `$${Math.abs(n).toFixed(decimals)}`;

const renderSignedCurrency = (
  value: number,
  {
    decimals = 4,
    negative = false,
    forceRed = false,
  }: { decimals?: number; negative?: boolean; forceRed?: boolean } = {}
) => {
  const absVal = Math.abs(value);
  const isNegative = forceRed || negative;
  const sign = absVal > 0 ? (isNegative ? '-' : '+') : '';
  const color = absVal === 0 ? 'text.primary' : isNegative ? 'error.main' : 'success.main';
  const fontWeight = absVal > 0 ? 600 : 400;
  return (
    <Typography component="span" variant="body2" sx={{ color, fontWeight }}>
      {`${sign}$${absVal.toFixed(decimals)}`}
    </Typography>
  );
};

const DetailRow = ({
  label,
  value,
  indent = false,
}: {
  label: string;
  value: React.ReactNode;
  indent?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      py: 0.75,
      pl: indent ? 2 : 0,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: indent ? 400 : 600, color: 'text.secondary' }}>
      {label}:
    </Typography>
    <Typography component="div" variant="body2" sx={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const SectionHeader = ({ label }: { label: string }) => (
  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mt: 2, mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.8 }}>
    {label}
  </Typography>
);

const EXTERNAL_FUNDING_SOURCES = new Set(['STRIPE_CARD', 'STRIPE_ACH', 'INTERNAL']);

const externalPaymentLabel = (fundingSource?: string | null) => {
  switch ((fundingSource || '').toUpperCase()) {
    case 'STRIPE_ACH':
      return 'ACH payment';
    case 'INTERNAL':
      return 'Internal payment';
    case 'STRIPE_CARD':
    default:
      return 'Card payment';
  }
};

/** Subject lines often include `| Payment: visa ***4242 | $20.00`. */
const parsePaymentMethodFromSubject = (subject?: string | null) => {
  if (!subject) return null;
  const match = subject.match(/\|\s*Payment:\s*([^|]+)/i);
  return match?.[1]?.trim() || null;
};

// ── main renderer ─────────────────────────────────────────────────────────────

interface TransactionDetailRendererProps {
  detail: TransactionDetailDto;
  /** Show audit-level fee rate inputs (for admin view) */
  showAuditFields?: boolean;
}

export const TransactionDetailRenderer: React.FC<TransactionDetailRendererProps> = ({
  detail,
  showAuditFields = false,
}) => {
  const { summary, identity, timing, classification, parties, amounts, fees, context, misc } = detail;
  const rawType = (summary.typeRaw || classification.subType || '').toUpperCase();
  const canonicalType = (classification.canonicalType || '').toUpperCase();
  const isPurchaseType = rawType === 'PURCHASE' || rawType === 'PRODUCT_PURCHASE' || rawType === 'CREDIT_PURCHASE';
  const isPaymentFastPayout =
    rawType === 'PAYOUT_SEND' ||
    rawType === 'PAYOUT_RECEIVE' ||
    canonicalType === 'PAYOUT' ||
    !!context.payout?.paymentId;
  const [feeScheduleOpen, setFeeScheduleOpen] = useState(false);

  const actor = parties.actor?.username || '';
  const counterparty = parties.counterparty?.username || '';
  const actorRole = parties.actor?.role || '';
  const isActorSource = actorRole === 'SENDER' || actorRole === 'BUYER';
  const sourceUser = isActorSource ? actor : counterparty;
  const recipientUser = isActorSource ? counterparty : actor;
  const feeScheduleName = fees.feeScheduleName || fees.feeVersionCode || 'N/A';
  const postageFeesTotal = fees.postageFeesTotal ?? (fees.components.deliveryFee + fees.components.mailServerFee);
  const attachedCreditsFeesTotal = fees.attachedCreditsFeesTotal ?? fees.components.clearingHouseFee;
  const amountDeltaActual = misc?.find((m) => m.key === 'Amount Delta (Actual)')?.value;
  const hiddenMiscKeys = new Set(['Amount Delta (Actual)']);
  const walletBalanceUnchanged = amounts.walletBalanceUnchanged === true;
  const systemFeeAmount = fees.systemFeeAmount ?? 0;
  const fundingSource = (classification.fundingSource || '').toUpperCase();
  const showExternalPayment =
    walletBalanceUnchanged &&
    systemFeeAmount > 0 &&
    (EXTERNAL_FUNDING_SOURCES.has(fundingSource) || !fundingSource);
  const paymentMethod = parsePaymentMethodFromSubject(summary.subject || context.subject);
  const externalPaymentRowLabel = externalPaymentLabel(classification.fundingSource);
  const amountDifference = walletBalanceUnchanged
    ? '0.0000'
    : amountDeltaActual != null
    ? (() => {
        const parsed = Number(amountDeltaActual);
        return Number.isFinite(parsed) ? `${parsed >= 0 ? '+' : ''}${parsed.toFixed(4)}` : amountDeltaActual;
      })()
    : `${amounts.amountDelta >= 0 ? '+' : ''}${amounts.amountDelta.toFixed(4)}`;
  const numericDifference = walletBalanceUnchanged
    ? 0
    : Number.isFinite(Number(amountDifference)) ? Number(amountDifference) : amounts.amountDelta;
  const isSpendingFlow = numericDifference < 0;
  const isPendingFlow = (summary.statusComputed || classification.status || '').toLowerCase() === 'pending';

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label={summary.typeDisplay || classification.displayType || classification.canonicalType} size="small" />
        <Chip label={summary.statusComputed || classification.status || 'completed'} size="small" color={summary.statusComputed === 'pending' ? 'warning' : 'success'} />
      </Box>

      <SectionHeader label="Top Summary" />
      <DetailRow label="Subject" value={summary.subject || context.subject || 'N/A'} />
      <DetailRow label="Transaction ID" value={<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{summary.transactionId || identity.transactionId}</Typography>} />
      <DetailRow label="Date & Time Initiated" value={fmtDateTime(summary.initiatedAtUtc || timing.initiatedAtUtc)} />
      <DetailRow label="Date & Time Completed" value={summary.completedAtUtc || timing.completedAtUtc ? fmtDateTime(summary.completedAtUtc || timing.completedAtUtc) : 'Pending'} />
      <DetailRow label="Status" value={summary.statusComputed || classification.status || 'completed'} />
      <DetailRow label="Type" value={summary.typeDisplay || classification.displayType || classification.canonicalType} />

      <SectionHeader label="Parties" />
      <DetailRow label="Source User" value={sourceUser || 'N/A'} />
      <DetailRow label="Recipient User" value={recipientUser || 'N/A'} />

      <SectionHeader label="Amounts" />
      <DetailRow
        label={isPaymentFastPayout ? 'Postage Credits' : 'Postage'}
        value={renderSignedCurrency(fees.components.deliveryFee, { decimals: 4, negative: isSpendingFlow || isPendingFlow })}
      />
      {!isPaymentFastPayout && (
        <DetailRow
          label="Attached Credits"
          value={renderSignedCurrency(amounts.attachedAmount, { decimals: 4, negative: isSpendingFlow || isPendingFlow })}
        />
      )}
      <DetailRow
        label="Reserved"
        value={renderSignedCurrency(amounts.reservedFunds, { decimals: 4, negative: isSpendingFlow || isPendingFlow })}
      />
      {showExternalPayment && (
        <DetailRow
          label={externalPaymentRowLabel}
          value={
            <Box sx={{ textAlign: 'right' }}>
              {renderSignedCurrency(systemFeeAmount, { decimals: 2, forceRed: true, negative: true })}
              {paymentMethod && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {paymentMethod}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                Charged externally; not added to Qix Credits.
              </Typography>
            </Box>
          }
        />
      )}

      <SectionHeader label="Fees" />
      <DetailRow label="Postage Fees" value={renderSignedCurrency(postageFeesTotal, { decimals: 4, forceRed: true })} />
      {!isPaymentFastPayout && (
        <DetailRow label="Attached Credits Fees" value={renderSignedCurrency(attachedCreditsFeesTotal, { decimals: 4, forceRed: true })} />
      )}
      {systemFeeAmount > 0 && (
        <DetailRow label="System Fee" value={renderSignedCurrency(systemFeeAmount, { decimals: 2, forceRed: true })} />
      )}
      <DetailRow
        label="Fee Schedule name"
        value={
          <Link component="button" variant="body2" onClick={() => setFeeScheduleOpen(true)} sx={{ textDecoration: 'underline' }}>
            {fees.resolutionFallback ? `${feeScheduleName} (fallback)` : feeScheduleName}
          </Link>
        }
      />

      <SectionHeader label="Account Balance" />
      {walletBalanceUnchanged && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Qix Credits balance unchanged; charge applied to card.
        </Typography>
      )}
      <DetailRow label="Account Balance Before" value={fmt(amounts.balanceBefore, 4) || '$0.0000'} />
      <DetailRow label="Difference" value={renderSignedCurrency(Math.abs(numericDifference), { decimals: 4, negative: numericDifference < 0 })} />
      {(amounts.vendorCreditsUsed ?? 0) > 0 && (amounts.regularCreditsUsed ?? 0) > 0 && (
        <DetailRow
          label="Vendor Credits"
          value={renderSignedCurrency(amounts.vendorCreditsUsed, { decimals: 4, forceRed: true })}
        />
      )}
      <DetailRow label="Account Balance After" value={fmt(amounts.balanceAfter, 4) || '$0.0000'} />

      <SectionHeader label="Purchase & Source" />
      {isPurchaseType && (
        <DetailRow label="Purchase Price" value={fmt(amounts.grossAmount) || '$0.00'} />
      )}
      <DetailRow
        label="Source URL"
        value={
          context.sourceUrl ? (
            <Link href={context.sourceUrl} target="_blank" rel="noopener" sx={{ fontSize: '0.8rem' }}>
              {context.sourceUrl.length > 50
                ? `${context.sourceUrl.substring(0, 50)}…`
                : context.sourceUrl}
            </Link>
          ) : 'N/A'
        }
      />

      {showAuditFields && (
        <>
          <SectionHeader label="Misc" />
          {misc?.filter((entry) => !hiddenMiscKeys.has(entry.key)).map((entry) => (
            <DetailRow key={`${entry.key}-${entry.value || ''}`} label={entry.key} value={entry.value || ''} />
          ))}
        </>
      )}

      {/* Linked IDs for cross-user/cross-portal reconciliation */}
      {(identity.linked.mailId || identity.linked.purchaseHistoryId || identity.linked.paymentHistoryId || identity.adjustmentSourceId) && (
        <>
          <SectionHeader label="Linked IDs" />
          {identity.linked.mailId && (
            <DetailRow
              label="Message ID"
              value={<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{identity.linked.mailId}</Typography>}
            />
          )}
          {identity.linked.purchaseHistoryId && (
            <DetailRow
              label="Purchase ID"
              value={<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{identity.linked.purchaseHistoryId}</Typography>}
            />
          )}
          {identity.linked.paymentHistoryId && (
            <DetailRow
              label="Payment ID"
              value={<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{identity.linked.paymentHistoryId}</Typography>}
            />
          )}
          {identity.adjustmentSourceId && (
            <DetailRow
              label="Source ID"
              value={<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{identity.adjustmentSourceId}</Typography>}
            />
          )}
        </>
      )}

      {/* Transaction ID footer */}
      <Divider sx={{ mt: 2, mb: 1 }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
        ID: {identity.transactionId}
      </Typography>

      <Dialog open={feeScheduleOpen} onClose={() => setFeeScheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Fee Schedule Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Schedule: <strong>{feeScheduleName}</strong>
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Message Postage Fees
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Postage fees are charged when messages are delivered.
          </Typography>
          <DetailRow label="Min Fixed Fee ($)" value={fees.inputs.fixed.mailServerDelivery.toFixed(4)} indent />
          <DetailRow
            label="Percentage Fee"
            value={`${(fees.inputs.percent.mailServerTransaction * 100).toFixed(2)}%`}
            indent
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Percentage fee is applied to the message postage amount.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
            Attached Credits Fees
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Attached Credits fees apply to credits attached to a message.
          </Typography>
          <DetailRow label="Min Fixed Fee ($)" value={fees.inputs.fixed.mailServerTransaction.toFixed(4)} indent />
          <DetailRow label="Threshold Amount ($)" value={fmt(fees.inputs.threshold)} indent />
          <DetailRow
            label="% Below Threshold"
            value={`${(fees.inputs.percent.clearingHouseUpToThreshold * 100).toFixed(2)}%`}
            indent
          />
          <DetailRow
            label="% Above Threshold"
            value={`${(fees.inputs.percent.clearingHouseOverThreshold * 100).toFixed(2)}%`}
            indent
          />
          {showAuditFields && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>Audit mode is enabled.</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeScheduleOpen(false)} variant="contained" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Loading / error state wrappers ────────────────────────────────────────────

export const TransactionDetailLoading: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
    <CircularProgress size={32} />
  </Box>
);

export const TransactionDetailError: React.FC<{ message?: string }> = ({ message }) => (
  <Alert severity="error" sx={{ mt: 1 }}>
    {message || 'Failed to load transaction details. Please try again.'}
  </Alert>
);
